import React from 'react';
import Typography from '../typography';
import { PolymorphicWithoutRef } from '../polymorphic';

export interface CollapsibleTextProps {
    children?: string
    className?: string
    maxLength?: number
    disabled?: boolean
}

const CollapsibleText = <Component extends React.ElementType = 'div'>({
    as,
    className,
    children = '',
    maxLength = 3,
    disabled,
}: PolymorphicWithoutRef<Component, CollapsibleTextProps>): JSX.Element => {
    const [open, setOpen] = React.useState(false);
    const [showButton, setShowButton] = React.useState(false);
    const containerRef = React.useRef<HTMLSpanElement>(null);

    const timeoutRef = React.useRef<NodeJS.Timeout>(null);

    const Component = as || 'div';

    React.useEffect(() => {
        const hasClamping = (el: HTMLSpanElement): boolean => {
            const { clientHeight, scrollHeight } = el;
            return clientHeight !== scrollHeight;
        };
        const createButtonCheck = (): void => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            (timeoutRef as React.MutableRefObject<NodeJS.Timeout>).current = setTimeout(() => {
                if (containerRef.current) {
                    // Save current state to reapply later if necessary.
                    const hadClampClass = containerRef.current.classList.contains('clamp-to-box');
                    // Make sure that CSS clamping is applied if aplicable.
                    if (!hadClampClass) containerRef.current.classList.add('clamp-to-box');
                    // Check for clamping and show or hide button accordingly.
                    setShowButton(hasClamping(containerRef.current));
                    // Sync clamping with local state.
                    if (!hadClampClass) containerRef.current.classList.remove('clamp-to-box');
                }
            }, 20);
        };
        // Run initially
        createButtonCheck();
        window.addEventListener('resize', createButtonCheck);
        return () => {
            window.removeEventListener('resize', createButtonCheck);
        };
    }, []);

    const isDangerous = typeof children === 'string';

    return (
        <>
            <Component className={className}>
                <span
                    ref={containerRef}
                    className="clamp-to-box"
                    style={{
                        '--clamp-to-box': open ? 'unset' : String(maxLength),
                    } as React.CSSProperties}
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={isDangerous ? { __html: children as string } : undefined}
                >
                    {!isDangerous ? children : undefined}
                </span>
            </Component>
            {(showButton && !disabled) && (
                <Typography
                    as="button"
                    onClick={() => setOpen(!open)}
                    color="primaryOrange"
                    css={{
                        marginTop: '8px',
                        position: 'relative',
                        transition: '0.3s',
                        '[role=group]:hover &': {
                            '&::before': {
                                transform: 'scale(1)',
                                right: '0',
                                transformOrigin: 'bottom left',
                            },
                        },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: '0',
                            height: '1px',
                            bottom: '-2px',
                            width: '100%',
                            backgroundColor: 'currentColor',
                            transition: '0.3s transform ease-out',
                            transform: 'scale(0)',
                            transformOrigin: 'bottom right',
                        },
                        '&:hover::before': {
                            transform: 'scale(1)',
                            right: '0',
                            transformOrigin: 'bottom left',
                        },
                        '&:hover': {
                            opacity: 0.8,
                        },
                    }}
                    fontSize={{
                        dt: 'base',
                        mb: 'sm',
                    }}
                >
                    Show
                    {' '}
                    {open ? 'less' : 'more'}
                </Typography>
            )}
        </>
    );
};
export default CollapsibleText;
